import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Socket, Channel } from 'phoenix';
import { LocalStorageService } from '../core/services/local-storage.service';
import { LocalStorageKeys } from '../core/constants/local-storage-keys.enum';
import { ShopCapabilityService } from './shop-capability.service';

export interface ImportStep {
  id: string;
  displayName: string;
  status: 'pending' | 'in-progress' | 'completed' | 'error' | 'skipped';
  progress: number;
  totalItems?: number;
  processedItems?: number;
  message?: string;
  order: number;
  startedAt?: number;
  completedAt?: number;
}

export interface OverallProgress {
  status: 'preparing' | 'in-progress' | 'completed' | 'failed';
  progress: number;
  currentStep?: string;
  totalSteps: number;
  completedSteps: number;
  startedAt: number;
  estimatedCompletionTime?: number;
  message?: string;
}

export interface ImportProgressData {
  merchantId: string;
  shopId: string;
  startedAt: number;
  stages: ImportStep[];
  overall: OverallProgress;
}

export interface ImportProgressEvent {
  type:
    | 'import.started'
    | 'import.progress'
    | 'import.completed'
    | 'import.core.completed'
    | 'import.failed'
    | 'step.started'
    | 'step.progress'
    | 'step.completed'
    | 'step.failed';
  merchantId: string;
  shopId: string;
  timestamp: number;
  data: {
    step?: ImportStep;
    stages?: ImportStep[];
    overall?: OverallProgress;
    error?: string;
    details?: any;
  };
}

export interface ActiveImportsData {
  merchantId: string;
  activeShops: string[];
  hasActiveImports: boolean;
  timestamp: number;
}

export interface ImportStatusChangedEvent {
  type: 'import.started' | 'import.completed' | 'import.failed';
  merchantId: string;
  shopId: string;
  timestamp: number;
  error?: string;
  showProgressModal?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ElixirImportProgressService implements OnDestroy {
  private socket: Socket | null = null;
  private channel: Channel | null = null;
  private globalChannel: Channel | null = null;
  private globalChannelRetryTimer: any = null;
  private connected$ = new BehaviorSubject<boolean>(false);
  private activeImports$ = new BehaviorSubject<ActiveImportsData | null>(null);
  private importProgress$ = new Subject<ImportProgressEvent>();
  private importStatusChanged$ = new Subject<ImportStatusChangedEvent>();
  private subscriptions = new Map<string, boolean>();
  private progressStateCache = new Map<string, ImportProgressEvent>();
  // Merchant ID will be set dynamically from import events
  private merchantId: string | null = null;
  // Track merchant ID per shop to handle multiple shops from different merchants
  private shopMerchantMap = new Map<string, string>();
  private lastHeartbeatTime = new Map<string, number>();
  private currentShopId: string | null = null;

  // Reconnection state
  private reconnectAttempt = 0;
  private reconnectTimer: any = null;
  private maxReconnectAttempts = Infinity; // Infinite reconnect attempts for multi-hour imports (was 10)
  private baseReconnectDelay = 1000; // 1 second
  private maxReconnectDelay = 30000; // 30 seconds

  // Throttle state for HTTP calls
  private lastActiveImportsCheck = 0;
  private activeImportsCheckThrottleMs = 5000; // Only check every 5 seconds

  constructor(
    private _httpClient: HttpClient,
    private localStorage: LocalStorageService,
    private ngZone: NgZone,
    private shopCapabilityService: ShopCapabilityService
  ) {
    // Auto-connect when service is created (Elixir is the only import backend)
    if (typeof window !== 'undefined') {
      // Small delay to ensure token is available
      setTimeout(() => {
        this.connect();
        this.subscribeToGlobalChannel();
      }, 100);
    }
  }

  ngOnDestroy() {
    this.disconnect();
  }

  /**
   * Connect to Phoenix WebSocket server
   */
  connect(): void {
    if (this.socket?.isConnected()) {
      return;
    }

    const token = this.localStorage.get<string>(LocalStorageKeys.ACCESS_TOKEN);

    if (!token) {
      console.warn('[ElixirImport] No JWT token found, cannot connect');
      return;
    }

    const baseUrl = environment.elixirApiUrl || 'http://localhost:4000';
    const wsUrl = baseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    const socketUrl = `${wsUrl}/socket`;

    // Create Phoenix Socket with auth token and exponential backoff
    this.socket = new Socket(socketUrl, {
      params: () => ({ token: this.localStorage.get(LocalStorageKeys.ACCESS_TOKEN) || token }),
      transport: WebSocket,
      timeout: 10000,
      reconnectAfterMs: (tries: number) => {
        return this.calculateReconnectDelay(tries);
      },
      heartbeatIntervalMs: 30000
    });

    // Expose socket on window for debugging
    (window as any).phoenixSocket = this.socket;

    this.socket.connect();
    this.setupEventListeners();
  }

  /**
   * Disconnect from Phoenix WebSocket server
   */
  disconnect(): void {
    // Clear any pending reconnect timers
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Clear global channel retry timer
    if (this.globalChannelRetryTimer) {
      clearTimeout(this.globalChannelRetryTimer);
      this.globalChannelRetryTimer = null;
    }

    // Leave shop-specific channel
    if (this.channel) {
      this.channel.leave();
      this.channel = null;
    }

    // Leave global channel
    if (this.globalChannel) {
      this.globalChannel.leave();
      this.globalChannel = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected$.next(false);
    }

    // Reset reconnect attempts
    this.reconnectAttempt = 0;
  }

  /**
   * Calculate reconnect delay with exponential backoff and jitter
   */
  private calculateReconnectDelay(attempt: number): number {
    this.reconnectAttempt = attempt;

    // Exponential backoff: 2^attempt * base delay
    const exponentialDelay = Math.min(
      this.baseReconnectDelay * Math.pow(2, attempt - 1),
      this.maxReconnectDelay
    );

    // Add jitter (0-25% of the delay) to prevent thundering herd
    const jitter = Math.random() * exponentialDelay * 0.25;
    const totalDelay = Math.floor(exponentialDelay + jitter);

    // Stop trying after max attempts
    if (attempt > this.maxReconnectAttempts) {
      this.handleReconnectFailure();
      return -1; // Phoenix Socket uses -1 to stop reconnecting
    }

    return totalDelay;
  }

  /**
   * Handle reconnection failure after max attempts
   */
  private handleReconnectFailure(): void {
    this.connected$.next(false);

    // Try to fall back to HTTP polling if available
    this.startHttpPolling();
  }

  /**
   * Start HTTP polling as fallback when WebSocket fails
   */
  private startHttpPolling(): void {
    // TODO: Implement HTTP polling as fallback
  }

  /**
   * Recover state after WebSocket reconnection
   */
  private recoverStateAfterReconnect(): void {
    // SECURITY FIX: Only recover if merchantId is known
    // Without merchantId, we can't verify shop ownership
    if (!this.merchantId) {
      // Re-subscribe to global channel first to get merchantId
      this.subscribeToGlobalChannel();
      return;
    }

    // Check for active imports in localStorage
    const importInfo = this.parseActiveImportFromStorage();
    if (importInfo && importInfo.startedAt) {
      const timeSinceStart = Date.now() - importInfo.startedAt;

      // If import was started less than 1 hour ago, re-subscribe
      // SECURITY: Verify localStorage merchantId matches current user
      if (timeSinceStart < 3600000) {
        if (importInfo.merchantId && importInfo.merchantId !== this.merchantId) {
          // Stale data from different user session — clear it
          this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);
        } else {
          this.subscribeToShop(importInfo.shopId);
          this.fetchCurrentImportStatus(importInfo.shopId);
        }
      } else {
        // Clear old import info
        this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);
      }
    }

    // Re-subscribe to previously subscribed shops
    const previousSubscriptions = Array.from(this.subscriptions.keys());
    if (previousSubscriptions.length > 0) {
      previousSubscriptions.forEach(shopId => {
        this.subscribeToShop(shopId);
      });
    }

    // Check for any active imports from backend
    this.checkActiveImportsFromBackend();
  }

  /**
   * Fetch current import status from backend
   */
  private fetchCurrentImportStatus(shopId: string): void {
    // Add cache-busting parameter to bypass browser cache
    const url = `${environment.elixirApiUrl}/api/imports/progress/${shopId}?t=${Date.now()}`;

    // Add headers to prevent caching
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache'
    });

    this._httpClient.get<any>(url, { headers, withCredentials: true }).subscribe({
      next: response => {
        if (response && response.status !== 'idle') {
          // Emit progress event with current status
          const progressEvent: ImportProgressEvent = {
            type: 'import.progress',
            merchantId: this.merchantId || '',
            shopId: shopId,
            timestamp: Date.now(),
            data: {
              overall: {
                status: response.status === 'completed' ? 'completed' : 'in-progress',
                progress: response.progress || 0,
                currentStep: response.current_component,
                totalSteps: 3,
                completedSteps: response.completed_components || 0,
                startedAt: response.started_at || Date.now()
              }
            }
          };

          this.importProgress$.next(progressEvent);
        }
      },
      error: error => {
        console.error(`[ElixirImport] Failed to fetch import status for shop ${shopId}:`, error);
      }
    });
  }

  /**
   * Check for active imports from backend
   */
  private checkActiveImportsFromBackend(): void {
    // CRITICAL FIX: Throttle calls to prevent excessive HTTP requests
    const now = Date.now();
    if (now - this.lastActiveImportsCheck < this.activeImportsCheckThrottleMs) {
      return;
    }
    this.lastActiveImportsCheck = now;

    // Fetch active imports from Elixir backend
    const url = `${environment.elixirApiUrl || 'http://localhost:4001'}/api/imports/active`;

    this._httpClient.get<any>(url).subscribe({
      next: response => {
        if (response && response.success && response.data && response.data.length > 0) {
          // Extract shop IDs from active imports
          const activeShops = response.data.map((imp: any) => imp.shop_id);
          const merchantId = response.data[0]?.merchant_id || '';

          // Update active imports observable
          this.activeImports$.next({
            merchantId: merchantId,
            activeShops: activeShops,
            hasActiveImports: true,
            timestamp: Date.now()
          });

          // Subscribe to each active shop's progress channel
          response.data.forEach((imp: any) => {
            const shopId = imp.shop_id;
            const merchantId = imp.merchant_id;

            if (shopId && merchantId) {
              // Store merchant ID for this shop
              this.shopMerchantMap.set(shopId, merchantId);

              // Subscribe to shop channel
              this.subscribeToShop(shopId);
            }
          });
        } else {
          // No active imports - clear any stale localStorage state
          // This prevents the banner from showing when import is already complete
          this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);

          // Update observable with empty state
          this.activeImports$.next({
            merchantId: '',
            activeShops: [],
            hasActiveImports: false,
            timestamp: Date.now()
          });
        }
      },
      error: err => {
        console.error('❌ [ElixirImport] Failed to fetch active imports:', err);

        // On error, also clear stale state to prevent stuck banners
        this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);

        // Update observable with empty state on error
        this.activeImports$.next({
          merchantId: '',
          activeShops: [],
          hasActiveImports: false,
          timestamp: Date.now()
        });
      }
    });
  }

  /**
   * Subscribe to global channel for all import events
   */
  private subscribeToGlobalChannel(): void {
    // CRITICAL FIX: Prevent infinite retry loop - check if already subscribed
    if (this.globalChannel) {
      // Already subscribed, clear any pending retry timers
      if (this.globalChannelRetryTimer) {
        clearTimeout(this.globalChannelRetryTimer);
        this.globalChannelRetryTimer = null;
      }
      return;
    }

    if (!this.socket?.isConnected()) {
      // Clear any existing timer before setting a new one
      if (this.globalChannelRetryTimer) {
        clearTimeout(this.globalChannelRetryTimer);
      }
      // Retry after connection
      this.globalChannelRetryTimer = setTimeout(() => this.subscribeToGlobalChannel(), 1000);
      return;
    }

    // Clear retry timer since we're connected now
    if (this.globalChannelRetryTimer) {
      clearTimeout(this.globalChannelRetryTimer);
      this.globalChannelRetryTimer = null;
    }

    // Join the global import channel
    this.globalChannel = this.socket.channel('import:global', {
      token: this.localStorage.get<string>(LocalStorageKeys.ACCESS_TOKEN)
    });

    // Handle all import events
    this.globalChannel.on('import_event', (payload: any) => {
      this.handleGlobalImportEvent(payload);
    });

    this.globalChannel.on('import.started', (payload: any) => {
      this.handleImportStarted(payload);
    });

    this.globalChannel.on('import.completed', (payload: any) => {
      this.handleImportCompleted(payload);
    });

    // Handle progress updates from Elixir
    this.globalChannel.on('progress_update', (payload: any) => {
      // Payload structure from Elixir:
      // {component: "products", timestamp: "...", data: {current: X, total: Y, percentage: Z, ...}}
      // For global channel progress updates, we don't know the shopId/merchantId yet
      // These events will be handled by the shop-specific channel subscription instead
      // Just log and skip for now
    });

    this.globalChannel
      .join()
      .receive('ok', (resp: any) => {
        // SECURITY FIX: Set merchantId from JWT-verified server response
        // This prevents cross-merchant channel subscriptions by ensuring
        // merchantId is set BEFORE any global import events arrive
        if (resp?.merchantId && !this.merchantId) {
          this.merchantId = resp.merchantId;
        }
      })
      .receive('error', (reason: any) => {
        // If join fails, reset global channel so it can retry
        this.globalChannel = null;
      });
  }

  /**
   * Handle global import events
   */
  private handleGlobalImportEvent(event: any): void {
    // Check if this is an import.started event
    if (event.type === 'import.started') {
      this.handleImportStarted(event);
    } else if (event.type === 'import.completed') {
      this.handleImportCompleted(event);
    } else if (event.type === 'import.failed') {
      this.handleImportFailed(event);
    }
  }

  /**
   * Handle import started event
   */
  private handleImportStarted(event: any): void {
    const shopId = event.shopId || event.shop_id || event.data?.shop_id;
    const merchantId = event.merchantId || event.merchant_id || event.data?.merchant_id;

    // SECURITY FIX: Skip events from other merchants
    // If this.merchantId is set, reject mismatches
    // If this.merchantId is NOT set, skip event entirely — do NOT adopt foreign merchantId
    if (this.merchantId) {
      if (merchantId && merchantId !== this.merchantId) {
        return;
      }
    } else {
      // merchantId not yet initialized (global channel join hasn't returned yet)
      // Do NOT process this event — it could be from any merchant
      return;
    }

    // Store merchant ID for this shop (safe — we verified it matches this.merchantId)
    if (shopId && merchantId) {
      this.shopMerchantMap.set(shopId, merchantId);
    }

    // Store in localStorage for recovery
    this.localStorage.set(
      LocalStorageKeys.ACTIVE_IMPORT,
      JSON.stringify({
        shopId: shopId,
        merchantId: merchantId,
        startedAt: Date.now(),
        backend: 'elixir'
      })
    );

    // Emit status changed event - wrap in NgZone for change detection
    this.ngZone.run(() => {
      const statusChangedEvent = {
        type: 'import.started' as const,
        merchantId: merchantId || '',
        shopId: shopId || '',
        timestamp: Date.now(),
        showProgressModal: event.showProgressModal
      };

      this.importStatusChanged$.next(statusChangedEvent);

      // Update active imports
      this.activeImports$.next({
        merchantId: merchantId || '',
        activeShops: [shopId],
        hasActiveImports: true,
        timestamp: Date.now()
      });
    });

    // Subscribe to shop-specific channel for detailed updates
    if (shopId) {
      this.subscribeToShop(shopId);
    }
  }

  /**
   * Handle import completed event
   */
  private handleImportCompleted(event: any): void {
    const shopId = event.shopId || event.shop_id || event.data?.shop_id;
    const merchantId = event.merchantId || event.merchant_id || event.data?.merchant_id;

    // SECURITY FIX: Skip events from other merchants or when merchantId not yet initialized
    if (!this.merchantId || (merchantId && merchantId !== this.merchantId)) {
      return;
    }

    // Clear from localStorage
    this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);

    // Emit status changed event - wrap in NgZone for change detection
    this.ngZone.run(() => {
      this.importStatusChanged$.next({
        type: 'import.completed',
        merchantId: event.merchantId || this.merchantId || '',
        shopId: shopId || '',
        timestamp: Date.now(),
        showProgressModal: event.showProgressModal
      });

      // Update active imports
      this.activeImports$.next({
        merchantId: event.merchantId || this.merchantId || '',
        activeShops: [],
        hasActiveImports: false,
        timestamp: Date.now()
      });
    });
  }

  /**
   * Handle import failed event
   */
  private handleImportFailed(event: any): void {
    const shopId = event.shopId || event.shop_id || event.data?.shop_id;
    const merchantId = event.merchantId || event.merchant_id || event.data?.merchant_id;

    // SECURITY FIX: Skip events from other merchants or when merchantId not yet initialized
    if (!this.merchantId || (merchantId && merchantId !== this.merchantId)) {
      return;
    }

    // Clear from localStorage
    this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);

    // Emit status changed event - wrap in NgZone for change detection
    this.ngZone.run(() => {
      this.importStatusChanged$.next({
        type: 'import.failed',
        merchantId: event.merchantId || this.merchantId || '',
        shopId: shopId || '',
        timestamp: Date.now(),
        error: event.error || event.data?.error,
        showProgressModal: event.showProgressModal
      });

      // Update active imports
      this.activeImports$.next({
        merchantId: event.merchantId || this.merchantId || '',
        activeShops: [],
        hasActiveImports: false,
        timestamp: Date.now()
      });
    });
  }

  /**
   * Subscribe to specific shop import progress
   */
  subscribeToShop(shopId: string): void {
    // SECURITY GUARD: Prevent subscribing with undefined/null shopId
    // This prevents users from accidentally subscribing to other merchants' channels
    if (!shopId || shopId === 'undefined' || shopId === 'null') {
      console.warn(`[ElixirImport] Cannot subscribe - invalid shopId: ${shopId}`);
      return;
    }

    if (!this.socket?.isConnected()) {
      console.warn(`[ElixirImport] Cannot subscribe to shop ${shopId} - socket not connected`);
      return;
    }

    // Check if already subscribed to this shop
    if (this.currentShopId === shopId && this.channel) {
      // Request status update for existing channel
      this.requestCurrentProgress(shopId);
      return;
    }

    // Leave previous channel if exists
    if (this.channel && this.currentShopId !== shopId) {
      this.channel.leave();
      this.channel = null;
    }

    this.currentShopId = shopId;

    // Get merchant ID for this shop from the map (set by import.started event)
    const shopMerchantId = this.shopMerchantMap.get(shopId);

    // CRITICAL FIX: If merchantId is not found, try to get it from HTTP API
    // This handles cases where modal opens before import.started event arrives
    if (!shopMerchantId) {
      console.warn(
        `[ElixirImport] No merchant ID found for shop ${shopId}. Attempting to fetch from API...`
      );

      // Try to get merchantId from active imports API
      this.getMerchantIdFromApi(shopId).then(merchantId => {
        if (merchantId) {
          this.shopMerchantMap.set(shopId, merchantId);
          // Retry subscription with merchantId
          this.subscribeToShopChannel(shopId, merchantId);
        } else {
          console.warn(
            `[ElixirImport] Could not get merchant ID for shop ${shopId}. Will wait for import.started event.`
          );
        }
      });

      return;
    }

    this.subscribeToShopChannel(shopId, shopMerchantId);
  }

  /**
   * Internal method to subscribe to shop channel (called after merchantId is available)
   */
  private subscribeToShopChannel(shopId: string, shopMerchantId: string): void {
    if (!this.socket?.isConnected()) {
      console.warn(`[ElixirImport] Cannot subscribe to shop channel - socket not connected`);
      return;
    }

    const importId = `${shopMerchantId}:${shopId}`;

    // Join the import progress channel
    this.channel = this.socket.channel(`import:progress:${importId}`, {
      token: this.localStorage.get<string>(LocalStorageKeys.ACCESS_TOKEN)
    });

    // Handle channel events
    this.channel.on('initial_state', (payload: any) => {
      this.handleInitialState(payload, shopId);
    });

    // Handle progress_update events - can be TWO types:
    // 1. From StateBridge.broadcast_full_progress: {stages: [...], overall: {...}}
    // 2. From UnifiedProgressTracker: {component: "products", data: {current, total, ...}}
    this.channel.on('progress_update', (payload: any) => {
      // Check if this is a full progress update from StateBridge (has stages + overall)
      if (payload.stages && payload.overall) {
        this.handleFullProgressUpdate(payload, shopId);
      } else if (payload.component) {
        this.handleProgressUpdate(payload, shopId);
      } else {
        console.warn('[Elixir] ❌ Unknown progress_update format:', payload);
        console.warn('[Elixir] ❌ Full payload:', JSON.stringify(payload, null, 2));
      }
    });

    this.channel.on('component_status', (payload: any) => {
      this.handleComponentStatus(payload, shopId);
    });

    this.channel.on('import_completed', (payload: any) => {
      this.handleShopImportCompleted(payload, shopId);
    });

    this.channel.on('import_error', (payload: any) => {
      this.handleImportError(payload, shopId);
    });

    // Join the channel
    this.channel
      .join()
      .receive('ok', (resp: any) => {
        this.subscriptions.set(shopId, true);
        this.connected$.next(true);

        // Request current state
        this.requestCurrentProgress(shopId);
      })
      .receive('error', (resp: any) => {
        // SECURITY FIX: On unauthorized rejection, clean up immediately
        // to prevent Phoenix auto-rejoin creating an infinite retry loop
        if (this.channel) {
          this.channel.leave();
          this.channel = null;
        }
        this.subscriptions.delete(shopId);
        this.shopMerchantMap.delete(shopId);
        if (this.currentShopId === shopId) {
          this.currentShopId = null;
        }
      })
      .receive('timeout', () => {
        // On timeout, clean up to prevent stale channel state
        if (this.channel) {
          this.channel.leave();
          this.channel = null;
        }
        this.subscriptions.delete(shopId);
        if (this.currentShopId === shopId) {
          this.currentShopId = null;
        }
      });
  }

  /**
   * Get merchant ID from API if not available
   */
  private async getMerchantIdFromApi(shopId: string): Promise<string | null> {
    try {
      // CRITICAL FIX: Use Node.js backend to get merchantId (avoids CORS issues with Elixir)
      // Node.js backend has /api/imports/progress/:shopId which returns merchantId
      const nodeBackendUrl = environment.apiUrl || 'http://localhost:3000/api';
      const url = `${nodeBackendUrl}/imports/progress/${shopId}`;

      try {
        const progressResponse = await this._httpClient.get<any>(url).toPromise();

        // Node.js backend returns progress data with merchantId
        if (progressResponse && progressResponse.merchantId) {
          return progressResponse.merchantId;
        }

        // Also check if merchantId is in the data object
        if (progressResponse && progressResponse.data && progressResponse.data.merchantId) {
          return progressResponse.data.merchantId;
        }
      } catch (e) {
        // Progress endpoint might not be available if no active import
        // Silently fail and try fallback
      }

      // Fallback: Try to get from localStorage (set when import starts)
      const importData = this.parseActiveImportFromStorage();
      if (importData && importData.shopId === shopId && importData.merchantId) {
        return importData.merchantId;
      }

      return null;
    } catch (error) {
      console.error(`[ElixirImport] Failed to get merchant ID for shop ${shopId}:`, error);
      return null;
    }
  }

  /**
   * Request current progress state for a shop
   */
  requestCurrentProgress(shopId: string): void {
    if (!this.channel) {
      return;
    }

    // Get merchant ID for this shop
    const shopMerchantId = this.shopMerchantMap.get(shopId);
    if (!shopMerchantId) {
      console.warn(
        `[ElixirImport] No merchant ID found for shop ${shopId} when requesting progress`
      );
      return;
    }

    this.channel
      .push('get_status', {
        merchant_id: shopMerchantId,
        shop_id: shopId
      })
      .receive('ok', (resp: any) => {
        this.handleInitialState(resp, shopId);
      })
      .receive('error', (err: any) => {});
  }

  /**
   * Unsubscribe from specific shop import progress
   */
  unsubscribeFromShop(shopId: string): void {
    if (this.channel && this.currentShopId === shopId) {
      this.channel.leave();
      this.channel = null;
      this.currentShopId = null;
    }
    this.subscriptions.delete(shopId);
    this.lastHeartbeatTime.delete(shopId);
    this.shopMerchantMap.delete(shopId);
  }

  /**
   * Subscribe to merchant-level events (not implemented in Phoenix yet)
   */
  subscribeToMerchant(): void {
    // Not needed for Phoenix implementation as we handle this differently
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): Observable<boolean> {
    return this.connected$.asObservable();
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.socket?.isConnected() || false;
  }

  /**
   * Get active imports
   */
  getActiveImports(): Observable<ActiveImportsData | null> {
    return this.activeImports$.asObservable();
  }

  /**
   * Get import progress events
   */
  getImportProgress(): Observable<ImportProgressEvent> {
    return this.importProgress$.asObservable();
  }

  /**
   * Get import status changed events
   */
  getImportStatusChanged(): Observable<ImportStatusChangedEvent> {
    return this.importStatusChanged$.asObservable();
  }

  /**
   * Setup WebSocket event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) {
      return;
    }

    // Socket connection events
    this.socket.onOpen(() => {
      this.connected$.next(true);

      // Reset reconnect attempts on successful connection
      this.reconnectAttempt = 0;

      // Merchant ID will be set from import.started events
      // Do not set hardcoded merchant ID here

      // Recover state after reconnection
      this.recoverStateAfterReconnect();
    });

    this.socket.onClose(() => {
      this.connected$.next(false);
      this.subscriptions.clear();
      // Reset global channel so it can rejoin after reconnection
      this.globalChannel = null;
      // Keep merchantId for reconnection attempts
      // this.merchantId = null; // Don't reset merchantId
    });

    this.socket.onError((error: any) => {
      this.connected$.next(false);
    });
  }

  /**
   * Handle initial state from Phoenix channel
   */
  private handleInitialState(payload: any, shopId: string): void {
    // If payload has no valid data and we have cached state, skip this update
    // This prevents overwriting good Elixir progress with empty fallback
    if ((!payload.stages || payload.stages.length === 0) && !payload.overall) {
      const cached = this.progressStateCache.get(shopId);
      if (cached && cached.data.stages && cached.data.stages.length > 0) {
        return;
      }
    }

    // Payload already comes in the correct format from backend
    const event: ImportProgressEvent = {
      type: 'import.progress',
      merchantId: payload.merchantId || this.merchantId || '',
      shopId: payload.shopId || shopId,
      timestamp: Date.now(),
      data: {
        stages: payload.stages || [],
        overall: payload.overall || {
          status: 'pending',
          progress: 0,
          currentStep: 'Initializing import...',
          totalSteps: 8, // ✅ FIXED: Use 8 stages, not 3!
          completedSteps: 0,
          startedAt: Date.now()
        }
      }
    };

    this.progressStateCache.set(shopId, event);

    // Wrap in NgZone to trigger Angular change detection
    this.ngZone.run(() => {
      this.importProgress$.next(event);
    });
  }

  /**
   * Handle progress update from Phoenix channel
   */
  private handleProgressUpdate(payload: any, shopId: string): void {
    // Payload from Elixir has structure: {component: "products", timestamp: "...", data: {current, total, percentage, ...}}
    const progressData = payload.data || payload;

    // Map component names to frontend expected IDs
    const componentMap: { [key: string]: string } = {
      products: 'products-catalog',
      orders: 'orders',
      statements: 'statements',
      orderStatements: 'order-statements',
      orderSkuTransactions: 'order-sku-transactions'
    };

    const componentId = componentMap[payload.component] || payload.component;

    // Skip FBT stages when capability is not granted
    const displayName = this.getDisplayName(payload.component);
    if (displayName === null) return;

    const event: ImportProgressEvent = {
      type: 'import.progress',
      merchantId: this.merchantId || '',
      shopId: shopId,
      timestamp: Date.now(),
      data: {
        // Include single step update for the specific component
        step: {
          id: componentId,
          displayName: displayName,
          status: this.mapComponentStatus(progressData.status || 'processing'),
          progress: progressData.percentage || 0,
          totalItems: progressData.total || 0,
          processedItems: progressData.current || progressData.processed || 0,
          message: progressData.message,
          order: this.getComponentOrder(payload.component),
          startedAt: Date.now()
        },
        overall: {
          status: 'in-progress',
          progress: progressData.percentage || 0,
          currentStep: `Processing ${payload.component}`,
          totalSteps: 8,
          completedSteps: 0,
          startedAt: Date.now(),
          message: `Processing ${payload.component}: ${progressData.current || progressData.processed || 0} / ${progressData.total || 0} items`
        }
      }
    };

    this.lastHeartbeatTime.set(shopId, Date.now());

    // Wrap in NgZone to trigger Angular change detection
    this.ngZone.run(() => {
      this.importProgress$.next(event);
    });
  }

  /**
   * Handle component status update from Phoenix channel
   * NOTE: This only updates individual step status, NOT overall progress!
   * Overall progress comes from import_progress events via StateBridge
   */
  private handleComponentStatus(payload: any, shopId: string): void {
    // Map component names to frontend expected IDs
    const componentMap: { [key: string]: string } = {
      products: 'products-catalog',
      orders: 'orders',
      statements: 'statements',
      orderStatements: 'order-statements',
      orderSkuTransactions: 'order-sku-transactions'
    };

    const componentId = componentMap[payload.component] || payload.component;

    // Skip FBT stages when capability is not granted
    const displayName = this.getDisplayName(payload.component);
    if (displayName === null) return;

    // Extract metadata for counts
    const metadata = payload.metadata || {};
    const current = metadata.current || payload.current || 0;
    const total = metadata.total || payload.total || 0;

    // Calculate progress for this specific step
    let progress = 0;
    if (payload.status === 'completed') {
      progress = 100;
    } else if (total > 0) {
      progress = Math.round((current / total) * 100);
    } else if (payload.progress !== undefined) {
      progress = payload.progress;
    }

    const event: ImportProgressEvent = {
      type: 'step.progress',
      merchantId: this.merchantId || '',
      shopId: shopId,
      timestamp: Date.now(),
      data: {
        // ONLY update step, NOT overall - overall comes from import_progress events
        step: {
          id: componentId,
          displayName: displayName,
          status: this.mapComponentStatus(payload.status),
          progress: progress,
          totalItems: total,
          processedItems: current,
          message: payload.message,
          order: this.getComponentOrder(payload.component),
          startedAt: Date.now()
        }
      }
    };

    // Wrap in NgZone to trigger Angular change detection
    this.ngZone.run(() => {
      this.importProgress$.next(event);
    });
  }

  /**
   * Handle full progress update from StateBridge with stages + overall data
   * This is the MAIN handler for real progress updates from Redis state
   */
  private handleFullProgressUpdate(payload: any, shopId: string): void {
    // Payload structure: {stages: [...], overall: {...}, merchantId, shopId, timestamp}
    const stages = payload.stages || [];
    const overall = payload.overall || {};

    // Convert stages to ImportStep[] format expected by frontend
    // Filter out FBT stages when capability is not granted
    const importSteps: ImportStep[] = stages
      .filter((stage: any) => this.getDisplayName(stage.id) !== null)
      .map((stage: any) => ({
        id: stage.id,
        displayName: stage.name || stage.displayName || this.getDisplayName(stage.id) || stage.id,
        status: this.mapComponentStatus(stage.status),
        progress: stage.progress || 0,
        totalItems: stage.totalItems || stage.total || 0,
        processedItems: stage.processedItems || stage.current || 0,
        message: stage.message,
        order: stage.order || this.getComponentOrder(stage.id),
        startedAt: stage.startedAt || Date.now(),
        completedAt: stage.completedAt
      }));

    // Build the event with BOTH stages and overall
    const event: ImportProgressEvent = {
      type: 'import.progress',
      merchantId: this.merchantId || payload.merchantId || '',
      shopId: shopId || payload.shopId,
      timestamp: payload.timestamp || Date.now(),
      data: {
        stages: importSteps,
        overall: {
          status: overall.status || 'in-progress',
          progress: overall.progress || 0,
          currentStep: overall.currentStep || 'Processing...',
          totalSteps: overall.totalSteps || stages.length,
          completedSteps: overall.completedSteps || 0,
          startedAt: overall.startedAt || Date.now(),
          estimatedCompletionTime: overall.estimatedCompletionTime,
          message: overall.message
        }
      }
    };

    this.lastHeartbeatTime.set(shopId, Date.now());

    // Emit the event with NgZone to trigger change detection
    this.ngZone.run(() => {
      this.importProgress$.next(event);
    });
  }

  /**
   * Handle shop-specific import completed
   */
  private handleShopImportCompleted(payload: any, shopId: string): void {
    const event: ImportProgressEvent = {
      type: 'import.completed',
      merchantId: this.merchantId || '',
      shopId: shopId,
      timestamp: Date.now(),
      data: {
        overall: {
          status: 'completed',
          progress: 100,
          currentStep: 'Import completed successfully!',
          totalSteps: 3,
          completedSteps: 3,
          startedAt: payload.started_at ? new Date(payload.started_at).getTime() : Date.now(),
          estimatedCompletionTime: Date.now(),
          message: 'Import completed successfully!'
        }
      }
    };

    // Wrap in NgZone to trigger Angular change detection
    this.ngZone.run(() => {
      this.importProgress$.next(event);
      this.importStatusChanged$.next({
        type: 'import.completed',
        merchantId: this.merchantId || '',
        shopId: shopId,
        timestamp: Date.now()
      });
    });
  }

  /**
   * Handle import error
   */
  private handleImportError(payload: any, shopId: string): void {
    const event: ImportProgressEvent = {
      type: 'import.failed',
      merchantId: this.merchantId || '',
      shopId: shopId,
      timestamp: Date.now(),
      data: {
        error: payload.error || 'Import failed',
        overall: {
          status: 'failed',
          progress: 0,
          currentStep: 'Import failed',
          totalSteps: 3,
          completedSteps: 0,
          startedAt: Date.now(),
          message: payload.error || 'Import failed'
        }
      }
    };

    // Wrap in NgZone to trigger Angular change detection
    this.ngZone.run(() => {
      this.importProgress$.next(event);
      this.importStatusChanged$.next({
        type: 'import.failed',
        merchantId: this.merchantId || '',
        shopId: shopId,
        timestamp: Date.now(),
        error: payload.error
      });
    });
  }

  /**
   * Parse active import data from localStorage
   * Handles both string and object formats returned by LocalStorageService
   */
  private parseActiveImportFromStorage(): any | null {
    const storedImport = this.localStorage.get<any>(LocalStorageKeys.ACTIVE_IMPORT);
    if (!storedImport) {
      return null;
    }

    try {
      // LocalStorageService.get() may return string or already-parsed object
      return typeof storedImport === 'string' ? JSON.parse(storedImport) : storedImport;
    } catch (e) {
      console.error('[ElixirImport] Failed to parse stored import data', e);
      this.localStorage.remove(LocalStorageKeys.ACTIVE_IMPORT);
      return null;
    }
  }

  /**
   * Helper functions to convert Phoenix format to expected format
   */
  private convertComponentsToStages(components: any): ImportStep[] {
    if (!components) return [];

    const stages: ImportStep[] = [];

    if (components.products) {
      stages.push({
        id: 'products-catalog',
        displayName: 'Importing Products Catalog',
        status: this.mapComponentStatus(components.products.status),
        progress: this.calculateComponentProgress(components.products),
        totalItems: components.products.count,
        processedItems: components.products.count,
        order: 1,
        startedAt: Date.now()
      });
    }

    if (components.orders) {
      stages.push({
        id: 'orders',
        displayName: 'Importing Orders',
        status: this.mapComponentStatus(components.orders.status),
        progress: this.calculateComponentProgress(components.orders),
        totalItems: components.orders.count,
        processedItems: components.orders.count,
        order: 2,
        startedAt: Date.now()
      });
    }

    if (components.statements) {
      stages.push({
        id: 'statements',
        displayName: 'Importing Statements',
        status: this.mapComponentStatus(components.statements.status),
        progress: this.calculateComponentProgress(components.statements),
        totalItems: components.statements.count,
        processedItems: components.statements.count,
        order: 3,
        startedAt: Date.now()
      });
    }

    return stages;
  }

  private mapStatus(status: string): 'preparing' | 'in-progress' | 'completed' | 'failed' {
    switch (status) {
      case 'ready':
      case 'pending':
        return 'preparing';
      case 'running':
      case 'processing':
        return 'in-progress';
      case 'completed':
      case 'done':
        return 'completed';
      case 'failed':
      case 'error':
        return 'failed';
      default:
        return 'preparing';
    }
  }

  private mapComponentStatus(
    status: string
  ): 'pending' | 'in-progress' | 'completed' | 'error' | 'skipped' {
    switch (status) {
      case 'pending':
      case 'ready':
        return 'pending';
      case 'in-progress': // Elixir backend sends 'in-progress' status
      case 'running':
      case 'processing':
        return 'in-progress';
      case 'completed':
      case 'done':
        return 'completed';
      case 'failed':
      case 'error':
        return 'error';
      case 'skipped':
        return 'skipped';
      default:
        return 'pending';
    }
  }

  private calculateComponentProgress(component: any): number {
    if (component.status === 'completed') return 100;
    if (component.status === 'failed') return 0;
    if (component.status === 'running' || component.status === 'processing') return 50;
    return 0;
  }

  private countCompletedSteps(components: any): number {
    if (!components) return 0;
    let count = 0;
    if (components.products?.status === 'completed') count++;
    if (components.orders?.status === 'completed') count++;
    if (components.statements?.status === 'completed') count++;
    return count;
  }

  private getDisplayName(component: string): string | null {
    switch (component) {
      case 'products':
      case 'products-catalog':
        return 'Importing Products Catalog';
      case 'products-details':
        return 'Product Details & Images';
      case 'products-inventory':
        return 'Product Inventory';
      case 'warehouses':
        return 'Importing Warehouses';
      case 'orders':
        return 'Importing Orders';
      case 'statements':
        return 'Importing Statements';
      case 'order-statements':
        return 'Statement Transactions';
      case 'order-sku-transactions':
        return 'Multi-SKU Transactions';
      case 'refunds':
        return 'Refunds & Returns';
      case 'fbt':
        if (!this.shopCapabilityService.hasFbtAccess) return null;
        return 'FBT Inventory';
      case 'fbt_inbound_orders':
        if (!this.shopCapabilityService.hasFbtAccess) return null;
        return 'FBT Inbound Orders';
      case 'analytics':
        return 'Analytics & Metrics';
      default:
        return component;
    }
  }

  private getComponentOrder(component: string): number {
    switch (component) {
      case 'products':
      case 'products-catalog':
        return 1;
      case 'products-details':
        return 2;
      case 'products-inventory':
        return 3;
      case 'warehouses':
        return 4;
      case 'orders':
        return 5;
      case 'statements':
        return 6;
      case 'order-statements':
        return 7;
      case 'order-sku-transactions':
        return 8;
      case 'refunds':
        return 9;
      case 'fbt':
        return 10;
      case 'fbt_inbound_orders':
        return 11;
      case 'analytics':
        return 12;
      default:
        return 0;
    }
  }

  getCurrentProgress(shopId: string): Observable<ImportProgressData | null> {
    // Get merchant ID for this shop from the map (set by import.started event)
    let shopMerchantId = this.shopMerchantMap.get(shopId);

    // If not in map, try to get from localStorage (for page refresh case)
    if (!shopMerchantId) {
      const importData = this.parseActiveImportFromStorage();
      if (importData && importData.shopId === shopId && importData.merchantId) {
        shopMerchantId = importData.merchantId;
        // Update the map for future use
        this.shopMerchantMap.set(shopId, importData.merchantId);
      }
    }

    // CRITICAL FIX: If merchantId is still not found, try to get it from API
    // This ensures we can load progress even if import.started event hasn't arrived yet
    if (!shopMerchantId) {
      console.warn(
        `[ElixirImport] No merchant ID found for shop ${shopId}, attempting to fetch from API...`
      );

      // Use async/await pattern to fetch merchantId first, then load progress
      return new Observable(observer => {
        this.getMerchantIdFromApi(shopId).then(merchantId => {
          if (merchantId) {
            this.shopMerchantMap.set(shopId, merchantId);
            const importId = `${merchantId}:${shopId}`;
            const elixirUrl = environment.elixirApiUrl || 'http://localhost:4001';

            this._httpClient.get<any>(`${elixirUrl}/api/imports/${importId}/progress`).subscribe({
              next: response => {
                // CRITICAL FIX: Elixir returns {success: true, data: {...}}, extract data field
                const progressData = response?.data || response;
                if (progressData && (progressData.stages || progressData.overall)) {
                  // Transform Elixir format to frontend format
                  const transformed: ImportProgressData = {
                    merchantId: merchantId,
                    shopId: shopId,
                    startedAt: progressData.overall?.startedAt || Date.now(),
                    stages: progressData.stages || [],
                    overall: progressData.overall || {
                      status: 'preparing',
                      progress: 0,
                      currentStep: '',
                      totalSteps: 0,
                      completedSteps: 0,
                      startedAt: Date.now()
                    }
                  };
                  observer.next(transformed);
                } else {
                  console.warn(
                    `[ElixirImport] Invalid progress data format for ${shopId}:`,
                    response
                  );
                  observer.next(null);
                }
              },
              error: err => {
                console.error(`[ElixirImport] Failed to load progress for ${shopId}:`, err);
                observer.next(null);
              },
              complete: () => observer.complete()
            });
          } else {
            console.warn(
              `[ElixirImport] Could not get merchant ID for shop ${shopId}, cannot load progress`
            );
            observer.next(null);
            observer.complete();
          }
        });
      });
    }

    const importId = `${shopMerchantId}:${shopId}`;
    const elixirUrl = environment.elixirApiUrl || 'http://localhost:4001';
    return this._httpClient.get<any>(`${elixirUrl}/api/imports/${importId}/progress`).pipe(
      map((response: any) => {
        // CRITICAL FIX: Elixir returns {success: true, data: {...}}, extract data field
        const progressData = response?.data || response;
        if (progressData && (progressData.stages || progressData.overall)) {
          // Transform Elixir format to frontend format
          return {
            merchantId: shopMerchantId,
            shopId: shopId,
            startedAt: progressData.overall?.startedAt || Date.now(),
            stages: progressData.stages || [],
            overall: progressData.overall || {
              status: 'preparing',
              progress: 0,
              currentStep: '',
              totalSteps: 0,
              completedSteps: 0,
              startedAt: Date.now()
            }
          } as ImportProgressData;
        }
        return null;
      })
    );
  }

  /**
   * Get the last heartbeat time for a shop
   */
  getLastHeartbeatTime(shopId: string): number | undefined {
    return this.lastHeartbeatTime.get(shopId);
  }

  /**
   * Check if a shop import is considered active based on heartbeat
   */
  isImportActiveByHeartbeat(shopId: string, maxInactivityMs = 60000): boolean {
    const lastHeartbeat = this.lastHeartbeatTime.get(shopId);
    if (!lastHeartbeat) {
      return false;
    }
    return Date.now() - lastHeartbeat < maxInactivityMs;
  }

  /**
   * Get cached progress for a shop (if available)
   * This is useful when modal opens after events have already been received
   */
  getCachedProgress(shopId: string): ImportProgressEvent | undefined {
    return this.progressStateCache.get(shopId);
  }
}
